import Archive from "../../Archive";
import "../../Home.css";
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import Navigation from "../../Navigation";
import Footer from "../../Footer";

const t = (value, language) => {
  if (typeof value === "string") return value;
  return value?.[language] || value?.en || "";
};

const architectureNodes = {
  admin: {
    position: { x: 22, y: 24 },
    plane: {
      en: "Control Plane",
      zh: "Control Plane",
      ja: "Control Plane",
    },
    label: {
      en: "Admin",
      zh: "Admin",
      ja: "Admin",
    },
    detail: {
      en: "control / review / overrides",
      zh: "控制 / 审核 / 覆写",
      ja: "制御 / レビュー / オーバーライド",
    },
    stack: {
      en: ["policy", "manual review", "incident controls"],
      zh: ["policy", "manual review", "incident controls"],
      ja: ["policy", "manual review", "incident controls"],
    },
  },
  service: {
    position: { x: 50, y: 50 },
    plane: {
      en: "Business Plane",
      zh: "Business Plane",
      ja: "Business Plane",
    },
    label: {
      en: "Service Layer",
      zh: "Service Layer",
      ja: "Service Layer",
    },
    detail: {
      en: "multiple services behind one write authority",
      zh: "多个 service，共享一个写权威",
      ja: "複数サービスだが write authority は 1 つ",
    },
    stack: {
      en: ["gateway", "auth", "user", "topic", "bet", "sms"],
      zh: ["gateway", "auth", "user", "topic", "bet", "sms"],
      ja: ["gateway", "auth", "user", "topic", "bet", "sms"],
    },
  },
  agent: {
    position: { x: 78, y: 24 },
    plane: {
      en: "Intelligence Plane",
      zh: "Intelligence Plane",
      ja: "Intelligence Plane",
    },
    label: {
      en: "Agent Layer",
      zh: "Agent Layer",
      ja: "Agent Layer",
    },
    detail: {
      en: "specialized agents for review, analysis, and ops",
      zh: "由多个 agent 处理审核、分析和运营信号",
      ja: "レビュー・分析・運用を担う複数 agent",
    },
    stack: {
      en: ["topic analyze", "internal review", "ops analytics", "daily hot"],
      zh: ["topic analyze", "internal review", "ops analytics", "daily hot"],
      ja: ["topic analyze", "internal review", "ops analytics", "daily hot"],
    },
  },
  bot: {
    position: { x: 50, y: 80 },
    plane: {
      en: "Execution Plane",
      zh: "Execution Plane",
      ja: "Execution Plane",
    },
    label: {
      en: "Bot Runtime",
      zh: "Bot Runtime",
      ja: "Bot Runtime",
    },
    detail: {
      en: "workers executing campaigns, comments, and distribution",
      zh: "执行 campaign、评论和分发的 bot workers",
      ja: "campaign・コメント・配信を実行する bot workers",
    },
    stack: {
      en: ["command workers", "comment bot", "heat engine", "campaign store"],
      zh: ["command workers", "comment bot", "heat engine", "campaign store"],
      ja: ["command workers", "comment bot", "heat engine", "campaign store"],
    },
  },
};

const architectureFlows = [
  {
    id: "control",
    number: "01",
    color: "#ff5f8f",
    title: {
      en: "Admin controls, services commit",
      zh: "Admin 发起控制，service 层负责落状态",
      ja: "Admin が制御し、service 群が状態を書き込む",
    },
    summary: {
      en: "Admin controls the flow, but the service layer owns the actual write path.",
      zh: "Admin 负责控制流程，真正的写路径仍在 service layer。",
      ja: "Admin はフローを制御するが、実際の write path は service layer が持つ。",
    },
    bullets: {
      en: [
        "Admin orchestrates review and overrides but does not own final writes",
        "Multiple services split responsibilities without splitting authority",
        "Settlement, refunds, and final events stay behind the service boundary",
      ],
      zh: [
        "Admin 负责编排 review 和 override，但不掌握最终写入",
        "多个 service 分工，但不会把最终权威也拆散",
        "结算、退款和最终事件仍然都留在 service 边界内",
      ],
      ja: [
        "Admin は review と override を編成するが最終 write は持たない",
        "複数 service に分かれても authority までは分裂しない",
        "精算・返金・最終イベントは service 境界の内側に残る",
      ],
    },
    nodes: ["admin", "service"],
    connections: [["admin", "service"]],
  },
  {
    id: "bot",
    number: "02",
    color: "#4cc9f0",
    title: {
      en: "Services dispatch the bot runtime",
      zh: "Service 层通过命令驱动 bot runtime",
      ja: "Service 群が bot runtime を駆動する",
    },
    summary: {
      en: "Services emit commands; the bot runtime turns them into campaigns, comments, and distribution.",
      zh: "service 层发命令，bot runtime 把它们变成 campaign、评论和分发。",
      ja: "service 群が command を出し、bot runtime がそれを campaign・コメント・配信に変える。",
    },
    bullets: {
      en: [
        "Service-side events schedule work instead of directly impersonating bots",
        "Bot workers report back into the system instead of freelancing state changes",
        "Allocation uses heat, pulse, and campaign context rather than fixed scripts",
      ],
      zh: [
        "service 侧事件只负责派发工作，不直接假装自己是 bot",
        "bot worker 会把结果回传，而不是各自改状态",
        "分配逻辑基于 heat、pulse 和 campaign context，而不是硬编码脚本",
      ],
      ja: [
        "service 側イベントは仕事を配るだけで bot を装わない",
        "bot worker は結果を戻し、勝手に状態を書き換えない",
        "配分ロジックは heat・pulse・campaign context に基づく",
      ],
    },
    nodes: ["service", "bot"],
    connections: [
      ["service", "bot"],
      ["bot", "service"],
    ],
  },
  {
    id: "agent",
    number: "03",
    color: "#8f7cff",
    title: {
      en: "Services consult a multi-agent layer",
      zh: "Service 层调一个多 agent 的 intelligence layer",
      ja: "Service 群が multi-agent layer を呼ぶ",
    },
    summary: {
      en: "A small agent layer scores, suggests, and reports back without taking over state ownership.",
      zh: "一个小型 agent layer 负责打分、建议和回传，但不会接管状态权威。",
      ja: "小さな agent layer がスコア付けと提案、返却を行うが、state ownership は奪わない。",
    },
    bullets: {
      en: [
        "Different agents specialize in review, generation, and operational signal",
        "Decisions remain advisory contracts rather than direct DB writes",
        "Audit and ingest flow back into services with explicit interfaces",
      ],
      zh: [
        "不同 agent 分别处理 review、generation 和 operational signal",
        "它们给出的 decision 仍然只是 advisory contract，不是直接改库",
        "audit 和 ingest 会通过明确接口回到 service 层",
      ],
      ja: [
        "複数 agent が review・generation・operational signal を分担する",
        "decision は advisory contract であり直接 DB を変えない",
        "audit と ingest は明示的な interface で service に戻る",
      ],
    },
    nodes: ["service", "agent"],
    connections: [
      ["service", "agent"],
      ["agent", "service"],
    ],
  },
  {
    id: "resolve",
    number: "04",
    color: "#36d399",
    title: {
      en: "Admin reviews exceptions, services finalize",
      zh: "边界 case 回到 Admin，最终结算仍在 service 层",
      ja: "例外は Admin へ戻し、最終確定は service 群が行う",
    },
    summary: {
      en: "Exceptions return to Admin, but final settlement still stays inside the service layer.",
      zh: "边界 case 回到 Admin，但最终结算仍然留在 service layer 内。",
      ja: "例外は Admin に戻るが、最終精算は service layer の内側に残る。",
    },
    bullets: {
      en: [
        "Human review is reserved for true escalations and policy boundaries",
        "Finalization stays with the services that already own market state",
        "Bot distribution only happens after the system has formally settled",
      ],
      zh: [
        "人工审核只保留给真正的 escalation 和 policy boundary",
        "finalization 仍然留给已经掌握 market state 的 service 层",
        "bot 的后续分发只会发生在系统正式结算之后",
      ],
      ja: [
        "人手レビューは本当に必要な escalation と policy boundary に限定",
        "finalization は market state を持つ service 群に残る",
        "bot の配信はシステムの正式精算後にだけ起こる",
      ],
    },
    nodes: ["admin", "service", "bot"],
    connections: [
      ["admin", "service"],
      ["service", "bot"],
      ["bot", "service"],
    ],
  },
];

const architectureInvariants = [
  {
    title: {
      en: "Many services, one authority",
      zh: "多 service，但只有一个最终权威",
      ja: "多 service でも authority は 1 つ",
    },
    body: {
      en: "Gateway, auth, user, topic, and bet services are split for clarity, but the market core still keeps a single business write contract.",
      zh: "gateway、auth、user、topic、bet 可以分开协作，但市场核心仍然保持统一的业务写 contract。",
      ja: "gateway・auth・user・topic・bet は分かれても、市場コアの business write contract は 1 つに保たれる。",
    },
  },
  {
    title: {
      en: "Many agents, one contract",
      zh: "多 agent，但只提供一种 contract",
      ja: "多 agent でも contract は明確",
    },
    body: {
      en: "Topic analysis, internal review, and ops agents can all suggest, score, and report. None of them silently become the source of truth.",
      zh: "topic analysis、internal review、ops agent 都可以打分、建议、回传信号，但没有谁会悄悄变成真相源。",
      ja: "topic analysis・internal review・ops agent は提案やスコア返却を行えるが、どれも黙って source of truth にはならない。",
    },
  },
  {
    title: {
      en: "Bot is a runtime, not a script",
      zh: "Bot 是运行时，不是一个脚本",
      ja: "Bot はスクリプトではなく runtime",
    },
    body: {
      en: "Command workers, comment participation, and heat allocation all run as inspectable background machinery instead of opaque one-off automation.",
      zh: "command worker、评论参与和 heat allocation 都是可检查的后台机制，而不是一堆不透明的一次性自动化。",
      ja: "command worker・コメント参加・heat allocation は、不透明な一発 automation ではなく検査可能な背景機構として動く。",
    },
  },
];

const KnewitArchitectureMap = ({ language }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((current) => (current + 1) % architectureFlows.length);
    }, 3600);

    return () => clearInterval(timer);
  }, []);

  const activeFlow = architectureFlows[activeIndex];

  const getLineStyle = (from, to) => {
    const start = architectureNodes[from].position;
    const end = architectureNodes[to].position;
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

    return {
      left: `${start.x}%`,
      top: `${start.y}%`,
      width: `${length}%`,
      transform: `translateY(-50%) rotate(${angle}deg)`,
    };
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.92fr_1.08fr]">
        <div className="text-left">
          <div className="mb-4 flex items-center justify-between border-b border-neutral-200 pb-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500">
              {t(
                {
                  en: "Interaction Choreography",
                  zh: "Interaction Choreography",
                  ja: "Interaction Choreography",
                },
                language,
              )}
            </p>
            <p className="text-xs text-neutral-400">
              {t(
                {
                  en: "Auto-cycling, tap to inspect",
                  zh: "自动轮播，可点击查看",
                  ja: "自動切り替え、クリックで確認",
                },
                language,
              )}
            </p>
          </div>

          <div className="space-y-3">
            {architectureFlows.map((flow, index) => {
              const isActive = index === activeIndex;

              return (
                <button
                  key={flow.id}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={`w-full border-l-2 px-0 py-3 text-left transition-all ${
                    isActive
                      ? "border-neutral-900 pl-4"
                      : "border-neutral-200 pl-3 hover:border-neutral-500 hover:pl-4"
                  }`}
                >
                  <div className="mb-1 flex items-center gap-3">
                    <span
                      className="text-xs font-semibold uppercase tracking-[0.18em]"
                      style={{ color: flow.color }}
                    >
                      {flow.number}
                    </span>
                    <span
                      className={`text-base font-medium ${
                        isActive ? "text-neutral-900" : "text-neutral-500"
                      }`}
                    >
                      {t(flow.title, language)}
                    </span>
                  </div>
                  {isActive && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="pt-2"
                    >
                      <p className="text-sm leading-relaxed text-neutral-600">
                        {t(flow.summary, language)}
                      </p>
                    </motion.div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="relative mx-auto w-full max-w-[520px]">
          <div className="relative aspect-square overflow-hidden border border-neutral-200 bg-white">
            <div
              className="absolute inset-0 opacity-40"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(17,24,39,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(17,24,39,0.05) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            ></div>
            <div
              className="absolute inset-x-10 top-8 h-32 rounded-full blur-3xl"
              style={{
                background: `radial-gradient(circle, ${activeFlow.color}18 0%, transparent 72%)`,
              }}
            ></div>

            {activeFlow.connections.map(([from, to]) => (
              <div
                key={`${activeFlow.id}-${from}-${to}`}
                className="absolute h-[2px] origin-left"
                style={getLineStyle(from, to)}
              >
                <motion.div
                  className="h-full w-full rounded-full"
                  style={{
                    background: `linear-gradient(90deg, transparent 0%, ${activeFlow.color} 45%, transparent 100%)`,
                    backgroundSize: "200% 100%",
                  }}
                  animate={{ backgroundPosition: ["160% 0%", "-80% 0%"] }}
                  transition={{
                    duration: 1.9,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                />
              </div>
            ))}

            {Object.entries(architectureNodes).map(([key, node]) => {
              const isActive = activeFlow.nodes.includes(key);

              return (
                <motion.div
                  key={key}
                  className="absolute"
                  style={{
                    left: `${node.position.x}%`,
                    top: `${node.position.y}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                  animate={
                    isActive
                      ? {
                          y: [0, -4, 0],
                          scale: [1, 1.02, 1],
                        }
                      : { y: 0, scale: 1 }
                  }
                  transition={{
                    duration: 2.2,
                    repeat: isActive ? Infinity : 0,
                    ease: "easeInOut",
                  }}
                >
                  <div className="relative">
                    {isActive && (
                      <motion.div
                        className="absolute inset-[-10px]"
                        style={{
                          background: `${activeFlow.color}12`,
                        }}
                        animate={{ opacity: [0.35, 0.7, 0.35] }}
                        transition={{ duration: 1.8, repeat: Infinity }}
                      />
                    )}

                    <div
                      className={`relative w-[120px] border px-3 py-3 text-left transition-all sm:w-[144px] sm:px-4 sm:py-4 md:w-[152px] ${
                        isActive
                          ? "border-neutral-900 bg-white"
                          : "border-neutral-200 bg-neutral-50"
                      }`}
                    >
                      <div
                        className="mb-2 h-2.5 w-2.5 rounded-full"
                        style={{
                          backgroundColor: isActive
                            ? activeFlow.color
                            : "#6b7280",
                        }}
                      ></div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400">
                        {t(node.plane, language)}
                      </p>
                      <p
                        className={`mt-1 text-sm font-semibold ${
                          isActive ? "text-neutral-900" : "text-neutral-700"
                        }`}
                      >
                        {t(node.label, language)}
                      </p>
                      <p className="mt-1 text-[11px] leading-relaxed text-neutral-500">
                        {t(node.detail, language)}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {node.stack[language].map((item) => (
                          <span
                            key={item}
                            className="border border-neutral-200 bg-white px-2 py-1 text-[10px] leading-none text-neutral-500"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}

            <div className="absolute inset-x-5 bottom-5 border border-neutral-200 bg-white/90 px-4 py-3 text-left">
              <p
                className="text-[11px] font-semibold uppercase tracking-[0.18em]"
                style={{ color: activeFlow.color }}
              >
                {t(
                  {
                    en: "Current Flow",
                    zh: "Current Flow",
                    ja: "Current Flow",
                  },
                  language,
                )}
              </p>
              <p className="mt-1 text-sm font-medium text-neutral-900">
                {t(activeFlow.title, language)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 border-t border-neutral-200 pt-4 md:grid-cols-3">
        {architectureInvariants.map((item) => (
          <div
            key={item.title.en}
            className="border border-neutral-200 bg-neutral-50 px-4 py-4 text-left"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-400">
              {t(item.title, language)}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-neutral-600">
              {t(item.body, language)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const KnewitPage = () => {
  const { language } = useLanguage();

  const pageCopy = {
    title: {
      en: "Knewit / Dongle",
      zh: "Knewit / 懂了",
      ja: "Knewit / Dongle",
    },
    kicker: {
      en: "From product-first app to coordinated runtime",
      zh: "从产品原型到协同运行时的重写",
      ja: "プロダクト初期形から協調ランタイムへの再設計",
    },
    intro: {
      en: "Knewit evolved from a product-first market app into a layered runtime: services handle state, agents advise, bots execute, and admin stays on control.",
      zh: "Knewit 从一个产品优先的 market app，演进成了分层运行时：services 管状态，agents 提建议，bots 做执行，admin 保留控制权。",
      ja: "Knewit は product-first な market app から、services が state を持ち、agents が助言し、bots が実行し、admin が制御を保つ layered runtime へ進化した。",
    },
    visit: {
      en: "Visit Website",
      zh: "访问网站",
      ja: "サイトを見る",
    },
    back: {
      en: "Back to Projects",
      zh: "返回项目列表",
      ja: "プロジェクトに戻る",
    },
    evolutionTitle: {
      en: "Product Evolution",
      zh: "Product Evolution",
      ja: "Product Evolution",
    },
    evolutionIntro: {
      en: "This was a system rewrite, not just a UI rewrite.",
      zh: "这次是系统重写，不只是界面重写。",
      ja: "これは UI ではなくシステムの書き換えだった。",
    },
    architectureTitle: {
      en: "System Architecture Evolution",
      zh: "System Architecture Evolution",
      ja: "System Architecture Evolution",
    },
    architectureIntro: {
      en: "What changed was ownership: who controls, who writes, who advises, and who runs in the background.",
      zh: "真正变化的是 ownership：谁控制、谁写状态、谁提供建议、谁在后台执行。",
      ja: "変わったのは ownership だ。誰が制御し、誰が書き込み、誰が助言し、誰が裏で動くのか。",
    },
    outcomesTitle: {
      en: "Rewrite Outcomes",
      zh: "Rewrite Outcomes",
      ja: "Rewrite Outcomes",
    },
  };

  const metrics = [
    {
      value: "5+ Services",
      label: {
        en: "one shared state contract",
        zh: "共享一套状态 contract",
        ja: "共有された state contract",
      },
    },
    {
      value: "Multi-Agent",
      label: {
        en: "advisory, not authoritative",
        zh: "负责建议，不负责裁决",
        ja: "助言するが裁決しない",
      },
    },
    {
      value: "Heat / Pulse",
      label: {
        en: "live bot allocation",
        zh: "实时 bot 分配",
        ja: "live bot allocation",
      },
    },
    {
      value: "Outbox + Kafka",
      label: {
        en: "replayable async control",
        zh: "可重放的异步控制",
        ja: "再実行可能な非同期制御",
      },
    },
  ];

  const evolutionPhases = [
    {
      id: "mvp",
      title: {
        en: "Mini-program MVP",
        zh: "Mini-program MVP",
        ja: "Mini-program MVP",
      },
      date: {
        en: "December 2024",
        zh: "2024年12月",
        ja: "2024年12月",
      },
      image: "/assets/projects/Knewit/before/before.png",
      body: {
        en: "The first version was a lightweight surface for topic discovery and low-friction participation.",
        zh: "最早的版本是一个轻量表层，重点是话题发现和低门槛参与。",
        ja: "初期版は、トピック発見と低摩擦な参加に集中した軽量な surface だった。",
      },
      points: {
        en: [
          "content-first onboarding",
          "IQ points instead of heavy financial UX",
        ],
        zh: ["内容优先的 onboarding", "用 IQ 点数降低金融感"],
        ja: ["content-first onboarding", "重い金融 UX ではなく IQ points"],
      },
    },
    {
      id: "rewrite",
      title: {
        en: "Service-driven rewrite",
        zh: "Service-driven rewrite",
        ja: "Service-driven rewrite",
      },
      date: {
        en: "February to May 2025",
        zh: "2025年2月到5月",
        ja: "2025年2月〜5月",
      },
      image: "/assets/projects/Knewit/after/after.png",
      body: {
        en: "The second version split the system into clear layers for control, state, intelligence, and execution.",
        zh: "第二版把系统拆成了控制、状态、智能和执行四层。",
        ja: "第 2 版では、システムを control・state・intelligence・execution の各層に分けた。",
      },
      points: {
        en: [
          "control moved into admin",
          "state authority stayed in the service layer",
        ],
        zh: ["控制收敛到 admin", "状态权威留在 service layer"],
        ja: [
          "control は admin へ",
          "state authority は service layer に残した",
        ],
      },
    },
  ];

  const siteUrl = "https://dongleapp.com";

  return (
    <div className="min-h-screen bg-white text-left text-neutral-900">
      <Navigation activeSection="projects" />
      <div className="px-6 pb-16 pt-24">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-8 flex items-center justify-between gap-4 border-b border-neutral-200 pb-5">
            <Link
              to="/projects"
              className="inline-flex items-center text-sm font-medium text-neutral-600 transition-colors hover:text-primary-600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-2 h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              {t(pageCopy.back, language)}
            </Link>

            <a
              href={siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-900 transition-colors hover:bg-neutral-50"
            >
              {t(pageCopy.visit, language)}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="ml-2 h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
              </svg>
            </a>
          </div>

          <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="border-b border-neutral-200 py-10 md:py-12"
          >
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.96fr_1.04fr] lg:items-center">
              <div>
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.22em] text-neutral-400">
                  {t(pageCopy.kicker, language)}
                </p>
                <h1 className="mb-5 text-4xl font-light leading-tight text-neutral-900 md:text-5xl lg:text-6xl">
                  {t(pageCopy.title, language)}
                </h1>
                <p className="max-w-2xl text-base leading-relaxed text-neutral-600 md:text-lg">
                  {t(pageCopy.intro, language)}
                </p>

                <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-5 border-t border-neutral-200 pt-6 md:grid-cols-4">
                  {metrics.map((metric) => (
                    <div key={metric.value}>
                      <div className="text-lg font-semibold text-neutral-900 md:text-xl">
                        {metric.value}
                      </div>
                      <div className="mt-1 text-xs leading-relaxed text-neutral-500">
                        {t(metric.label, language)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="overflow-hidden border border-neutral-200 bg-neutral-50">
                <img
                  src="/assets/projects/Knewit/cover.png"
                  alt="Knewit overview"
                  className="h-full w-full object-cover object-center"
                />
              </div>
            </div>
          </motion.section>

          <section className="py-16">
            <div className="mb-8 max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-neutral-400">
                {t(pageCopy.evolutionTitle, language)}
              </p>
              <h2 className="mt-3 text-3xl font-light text-neutral-900 md:text-4xl">
                {t(pageCopy.evolutionTitle, language)}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-neutral-600">
                {t(pageCopy.evolutionIntro, language)}
              </p>
            </div>

            <div className="space-y-8">
              {evolutionPhases.map((phase, index) => (
                <motion.article
                  key={phase.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.25 }}
                  transition={{ duration: 0.45, delay: index * 0.08 }}
                  className="grid grid-cols-1 gap-6 border-t border-neutral-300 py-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-center"
                >
                  <div
                    className={`${index % 2 === 1 ? "lg:order-2" : ""} flex flex-col justify-center`}
                  >
                    <div className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-neutral-400">
                      {t(phase.date, language)}
                    </div>
                    <h3 className="text-2xl font-medium text-neutral-900">
                      {t(phase.title, language)}
                    </h3>
                    <p className="mt-3 max-w-2xl text-base leading-relaxed text-neutral-600">
                      {t(phase.body, language)}
                    </p>

                    <div className="mt-5 space-y-2">
                      {phase.points[language].map((point) => (
                        <div
                          key={point}
                          className="flex items-start gap-2 text-sm leading-relaxed text-neutral-700"
                        >
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-neutral-900"></span>
                          <span>{point}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                    <div className="flex items-center justify-center">
                      <img
                        src={phase.image}
                        alt={t(phase.title, language)}
                        className="h-auto w-[64%] max-w-[340px] object-contain object-center"
                      />
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </section>

          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="border-y border-neutral-200 py-12"
          >
            <div className="mb-8 max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-neutral-400">
                {t(pageCopy.architectureTitle, language)}
              </p>
              <h2 className="mt-3 text-3xl font-light text-neutral-900 md:text-4xl">
                {t(pageCopy.architectureTitle, language)}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-neutral-600">
                {t(pageCopy.architectureIntro, language)}
              </p>
            </div>

            <KnewitArchitectureMap language={language} />
          </motion.section>

        </div>
      </div>
      <div className="chain-page project-archive"><Archive project="Knewit" /></div>
      <Footer />
    </div>
  );
};

export default KnewitPage;
